import React, { FunctionComponent, useState } from 'react';
import { Field } from 'react-final-form';
import { Typography } from '@mui/material';
import i18next from 'i18next';

import {
  composeValidators,
  digitsOnly,
  fileValidation,
  requiredValidation,
  onlyLetters,
} from 'helpers/validations';
import { CURRENCY_OPTIONS } from 'helpers/currencies';

import MultiStepForm from 'components/commons/MultiStepForm';
import Input from 'components/inputs/Input';
import SelectInput from 'components/inputs/SelectInput';

import { ViewContainer, ViewContent, Title } from 'styles/view';
import { FormRowContainer } from 'styles/form';
import FieldRequired from 'components/commons/FieldRequired';

import { IReservationsFormData, IReservationsFormProps } from './types';
import './i18n';

const ReservationsForm: FunctionComponent<IReservationsFormProps> = (
  props: IReservationsFormProps,
) => {
  const { cementeries, civilStates, files, idCardTypes, initialValues, isEditing, onSubmit } =
    props;
  const [formValues, setFormValues] = useState<IReservationsFormData>(initialValues);

  const tabs = [
    i18next.t<string>('RESERVATIONS_FORM:MANAGER'),
    i18next.t<string>('RESERVATIONS_FORM:FAMILY'),
    i18next.t<string>('RESERVATIONS_FORM:CHILDREN'),
    i18next.t<string>('RESERVATIONS_FORM:LOCATION'),
    i18next.t<string>('RESERVATIONS_FORM:PERSON'),
    i18next.t<string>('RESERVATIONS_FORM:PAYMENT'),
    i18next.t<string>('RESERVATIONS_FORM:COMMENTS'),
  ];

  return (
    <ViewContainer auth data-testid="reservations-form">
      <Title>{i18next.t<string>('RESERVATIONS_FORM:TITLE')}</Title>
      <ViewContent auth>
        <MultiStepForm<IReservationsFormData>
          initialValues={initialValues}
          onSubmit={onSubmit}
          onValuesChange={setFormValues}
          tabs={tabs}
          tabsEnabled={isEditing}
        >
          <MultiStepForm.Page key="manager-page">
            <Typography variant="h6">
              {i18next.t<string>('RESERVATIONS_FORM:MANAGER_INFORMATION')}
            </Typography>
            <br />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('RESERVATIONS_FORM:LAST_NAME')}
                name="person.lastName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
              <FieldRequired
                label={i18next.t<string>('RESERVATIONS_FORM:NAME')}
                name="person.name"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('RESERVATIONS_FORM:ID_CARD_TYPE')}
                name="person.idCardType"
                options={idCardTypes}
                render={SelectInput}
                validate={requiredValidation}
              />
              <FieldRequired
                label={i18next.t<string>('RESERVATIONS_FORM:ID_CARD')}
                name="person.idCard"
                render={Input}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('RESERVATIONS_FORM:ID_CARD_FILE')}
                name="person.idCardFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('RESERVATIONS_FORM:ID_CARD_FILE'),
                    value: files?.personIdCardFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
              <FieldRequired
                label={i18next.t<string>('RESERVATIONS_FORM:ID_CARD_BACK_FILE')}
                name="person.idCardBackFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('RESERVATIONS_FORM:ID_CARD_BACK_FILE'),
                    value: files?.personIdCardBackFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('RESERVATIONS_FORM:ADDRESS')}
                name="person.address"
                render={Input}
                validate={requiredValidation}
              />
              <FieldRequired
                label={i18next.t<string>('RESERVATIONS_FORM:AGE')}
                name="person.age"
                render={Input}
                validate={requiredValidation}
                type="number"
              />
            </FormRowContainer>
            <FormRowContainer>
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('RESERVATIONS_FORM:AREA_CODE')}
                  maxLength={4}
                  name="person.areaCode"
                  parse={(v: string) => digitsOnly(v)}
                  render={Input}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('RESERVATIONS_FORM:PHONE')}
                  maxLength={8}
                  name="person.phone"
                  parse={(v: string) => digitsOnly(v)}
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('RESERVATIONS_FORM:COMMUNITY')}
                name="person.community"
                render={Input}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('RESERVATIONS_FORM:CIVIL_STATE')}
                name="person.civilState"
                options={civilStates}
                render={SelectInput}
                validate={requiredValidation}
              />
              <FieldRequired
                label={i18next.t<string>('RESERVATIONS_FORM:WEDDING_TEMPLE')}
                name="person.weddingTemple"
                render={Input}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('RESERVATIONS_FORM:KETUBA_FILE')}
                name="person.ketubaFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('RESERVATIONS_FORM:KETUBA_FILE'),
                    value: files?.personKetubaFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing || formValues.person.civilState !== 'MARRIED'
                    ? fileValidation
                    : composeValidators(requiredValidation, fileValidation)
                }
              />
              <Field
                label={i18next.t<string>('RESERVATIONS_FORM:MARRIAGE_CERTIFICATE_FILE')}
                name="person.marriageCertificateFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('RESERVATIONS_FORM:MARRIAGE_CERTIFICATE_FILE'),
                    value: files?.personMarriageCertificateFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing || formValues.person.civilState !== 'MARRIED'
                    ? fileValidation
                    : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>
            {formValues.person.civilState === 'DIVORCED' && (
              <FieldRequired
                label={i18next.t<string>('RESERVATIONS_FORM:GET_FILE')}
                name="person.getFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('RESERVATIONS_FORM:GET_FILE'),
                    value: files?.personGetFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
            )}
          </MultiStepForm.Page>
          <MultiStepForm.Page key="family-page">
            <Typography variant="h6">
              {i18next.t<string>('RESERVATIONS_FORM:FAMILY_INFORMATION')}
            </Typography>
            <br />
            <FieldRequired
              label={i18next.t<string>('RESERVATIONS_FORM:SPOUSE_INFO')}
              name="family.spouseInfo"
              render={Input}
              type="textarea"
              validate={requiredValidation}
            />
            <FieldRequired
              label={i18next.t<string>('RESERVATIONS_FORM:FATHER_INFO')}
              name="family.fatherInfo"
              render={Input}
              type="textarea"
              validate={requiredValidation}
            />
            <FieldRequired
              label={i18next.t<string>('RESERVATIONS_FORM:MOTHER_INFO')}
              name="family.motherInfo"
              render={Input}
              type="textarea"
              validate={requiredValidation}
            />
          </MultiStepForm.Page>
          <MultiStepForm.Page key="children-page">
            <Typography variant="h6">
              {i18next.t<string>('RESERVATIONS_FORM:CHILDREN_INFORMATION')}
            </Typography>
            <br />
            <FieldRequired
              label={i18next.t<string>('RESERVATIONS_FORM:CHILDREN_INFO')}
              name="family.childrenInfo"
              render={Input}
              type="textarea"
              validate={requiredValidation}
            />
          </MultiStepForm.Page>
          <MultiStepForm.Page key="location-page">
            <Typography variant="h6">
              {i18next.t<string>('RESERVATIONS_FORM:LOCATION_INFORMATION')}
            </Typography>
            <br />
            <FormRowContainer>
              <Field
                label={i18next.t<string>('RESERVATIONS_FORM:CEMENTERY')}
                name="location.cementery"
                options={cementeries}
                render={SelectInput}
                validate={requiredValidation}
              />
            </FormRowContainer>
            {formValues.location?.cementery === 'OLD' && (
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('RESERVATIONS_FORM:SECTOR')}
                  name="location.sector"
                  render={Input}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('RESERVATIONS_FORM:PLANK')}
                  name="location.plank"
                  render={Input}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('RESERVATIONS_FORM:ROW')}
                  name="location.row"
                  render={Input}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('RESERVATIONS_FORM:GRAVE')}
                  name="location.grave"
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
            )}

            {/* Caso 2: ANEXO */}
            {formValues.location?.cementery === 'ANNEX' && (
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('RESERVATIONS_FORM:PLANK')}
                  name="location.plank"
                  render={Input}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('RESERVATIONS_FORM:GRAVE')}
                  name="location.grave"
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
            )}

            {/* Caso 3: CANNING */}
            {formValues.location?.cementery === 'CANNING' && (
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('RESERVATIONS_FORM:BLOCK')}
                  name="location.block"
                  render={Input}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('RESERVATIONS_FORM:ROW')}
                  name="location.row"
                  render={Input}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('RESERVATIONS_FORM:GRAVE')}
                  name="location.grave"
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
            )}
          </MultiStepForm.Page>
          <MultiStepForm.Page key="manager-page">
            <Typography variant="h6">
              {i18next.t<string>('RESERVATIONS_FORM:PERSON_INFORMATION')}
            </Typography>
            <br />
            <FieldRequired
              label={i18next.t<string>('RESERVATIONS_FORM:NAME')}
              name="manager.name"
              render={Input}
              validate={composeValidators(requiredValidation, onlyLetters)}
            />
            <FormRowContainer>
              <Field
                label={i18next.t<string>('RESERVATIONS_FORM:ID_CARD_TYPE')}
                name="manager.idCardType"
                options={idCardTypes}
                render={SelectInput}
                validate={requiredValidation}
              />
              <FieldRequired
                label={i18next.t<string>('RESERVATIONS_FORM:ID_CARD')}
                name="manager.idCard"
                render={Input}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('RESERVATIONS_FORM:ID_CARD_FILE')}
                name="manager.idCardFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('RESERVATIONS_FORM:ID_CARD_FILE'),
                    value: files?.managerIdCardFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
              <FieldRequired
                label={i18next.t<string>('RESERVATIONS_FORM:ID_CARD_BACK_FILE')}
                name="manager.idCardBackFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('RESERVATIONS_FORM:ID_CARD_BACK_FILE'),
                    value: files?.managerIdCardBackFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('RESERVATIONS_FORM:ADDRESS')}
                name="manager.address"
                render={Input}
                validate={requiredValidation}
              />
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('RESERVATIONS_FORM:AREA_CODE')}
                  maxLength={4}
                  name="manager.areaCode"
                  parse={(v: string) => digitsOnly(v)}
                  render={Input}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('RESERVATIONS_FORM:PHONE')}
                  maxLength={8}
                  name="manager.phone"
                  parse={(v: string) => digitsOnly(v)}
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
            </FormRowContainer>
            <FieldRequired
              label={i18next.t<string>('RESERVATIONS_FORM:RELATIONSHIP')}
              name="manager.relationship"
              render={Input}
              validate={requiredValidation}
            />
          </MultiStepForm.Page>
          <MultiStepForm.Page key="payment-page">
            <Typography variant="h6">
              {i18next.t<string>('RESERVATIONS_FORM:PAYMENT_INFORMATION')}
            </Typography>
            <br />
            <FormRowContainer>
              <Field
                label={i18next.t<string>('RESERVATIONS_FORM:CURRENCY')}
                name="payment.currency"
                options={CURRENCY_OPTIONS}
                render={SelectInput}
                validate={requiredValidation}
              />
              <FieldRequired
                label={i18next.t<string>('RESERVATIONS_FORM:AMOUNT')}
                name="payment.amount"
                parse={(v: string) => digitsOnly(v)}
                render={Input}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FieldRequired
              label={i18next.t<string>('RESERVATIONS_FORM:PAYMENT_METHOD')}
              name="payment.paymentMethod"
              render={Input}
              validate={requiredValidation}
            />
          </MultiStepForm.Page>
          <MultiStepForm.Page key="comments-page">
            <Field
              label={i18next.t<string>('RESERVATIONS_FORM:COMMENTS')}
              name="comments"
              parse={(value) => value}
              render={Input}
              type="textarea"
            />
          </MultiStepForm.Page>
        </MultiStepForm>
      </ViewContent>
    </ViewContainer>
  );
};

export default ReservationsForm;
